import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { matchRoutes, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { qrcodeAPIService } from '~/api/qrcode';
import { BackdropFullScreen } from '~/components/atoms/loader/BackdropFullScreen';
import { VCardMockup } from '~/components/atoms/mockup-previews/vcard-mockup/VCardMockup';
import { ConfirmNavigateModal } from '~/components/organisms/ConfirmNavigateModal';
import { QR_CODE_FOLDER_FORM_NAME, QRCODE_STATIC_STEP, QRCODE_STATIC_TYPE } from '~/constants/qr-code';
import { PATH } from '~/constants/route-path';
import useQrCodeData from '~/hooks/useQrCodeData';
import { useZodForm } from '~/hooks/useZodForm';
import { StaticQrContentLayout } from '~/pages/qrBuilder/p-static/components/static-qr-content-layout/StaticQrContentLayout';
import { StaticQrCodeData } from '~/types/qrCode';
import { mapQrTypeForApi } from '~/utils/qr-code';

import { StaticVCardForm } from './components/form/Form';
import { DEFAULT_VALUES, getSchema } from './utils/form-schema';
import { transformValuesToContent } from './utils/transform-values-to-content';

export const EditStaticVCardContent = () => {
  const navigate = useNavigate();
  const form = useZodForm({ getSchema, defaultValues: DEFAULT_VALUES });
  const [{ params }] = matchRoutes([{ path: PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.CONTENT) }], location);
  const values = form.watch();

  const { data: qrCode, isLoading: qrCodeIsLoading } = useQrCodeData<StaticQrCodeData>({ qrCodeId: params.id });

  const isChangeQrType = params.type !== qrCode?.content.type;

  const changeStaticQrTypeMutation = useMutation({ mutationFn: qrcodeAPIService.changeStaticQrType });
  const updateStaticQrVCardMutation = useMutation({ mutationFn: qrcodeAPIService.updateStaticQrVCard });

  const isNextProcessing = changeStaticQrTypeMutation.isPending || updateStaticQrVCardMutation.isPending;

  useEffect(() => {
    if (qrCode) {
      if (!isChangeQrType) {
        const { firstName, lastName, email, website, companyName, companyPosition, phones, location } = qrCode.content;
        const { type, url, longitude, latitude, street, number, postalCode, city, state, country } = location;

        form.setValue('firstName', firstName || '');
        form.setValue('lastName', lastName || '');
        form.setValue('email', email || '');
        form.setValue('website', website || '');
        form.setValue('companyName', companyName || '');
        form.setValue('companyPosition', companyPosition || '');
        form.setValue('phones', phones || []);

        form.setValue('location.street', street || '');
        form.setValue('location.postalCode', postalCode || '');
        form.setValue('location.number', number ? String(number) : undefined);
        form.setValue('location.city', city || '');
        form.setValue('location.state', state || '');
        form.setValue('location.country', country || '');
        form.setValue('location.latitude', latitude || '');
        form.setValue('location.longitude', longitude || '');

        if (url) {
          form.setValue('location.url', url);
        }

        form.setValue('location.type', type || '');
      }

      form.setValue('name', qrCode.name);
      // form.setValue('passwordProtection.isPasswordProtected', qrCode.isPasswordProtected ?? false);

      if (qrCode.folder) {
        form.setValue(QR_CODE_FOLDER_FORM_NAME, qrCode.folder.id);
      }
    }
  }, [qrCode?.id]);

  const onSubmit = (values) => {
    if (isNextProcessing) return;

    const { name } = values;

    const payload = {
      ...(isChangeQrType ? { type: mapQrTypeForApi(params.type) } : {}),
      id: params.id,
      body: {
        name,
        content: transformValuesToContent(values),
        ...(values.qrCodeFolder ? { folder_id: values.qrCodeFolder } : {}),
        // isPasswordProtected: values.passwordProtection.isPasswordProtected,
        // password: values.passwordProtection.password,
      },
    };

    const mutation = isChangeQrType ? changeStaticQrTypeMutation : updateStaticQrVCardMutation;

    mutation.mutate(payload, {
      onSuccess: () => {
        navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.STYLES, QRCODE_STATIC_TYPE.VCARD, params.id));
      },
    });
  };

  const handleClickStep = async (step) => {
    if (step === 0) navigate(PATH.QR.EDIT_QR_STATIC(QRCODE_STATIC_STEP.TYPE, QRCODE_STATIC_TYPE.VCARD, params.id));
    if (step === 2) await form.handleSubmit(onSubmit)();
  };

  return (
    <FormProvider {...form}>
      <StaticQrContentLayout
        handleStep={handleClickStep}
        isNextProcessing={isNextProcessing}
        activeStep={1}
        qrCode={qrCode}
        mockup={<VCardMockup values={values} />}
        form={
          <BackdropFullScreen isLoading={qrCodeIsLoading}>
            <StaticVCardForm />
          </BackdropFullScreen>
        }
      />

      <ConfirmNavigateModal activeStep={1} />
    </FormProvider>
  );
};
